import { useState, useEffect, useRef, useCallback } from "react";

// ── Physics ───────────────────────────────────────────────────────────────────
const MU=3.986e14, RE=6371e3, G0=9.81, ISP=70, M=12;
const r0=RE+450e3;
const vc0=Math.sqrt(MU/r0);
const T_ORB=2*Math.PI*Math.sqrt(r0**3/MU); // 93.44 min in seconds
const DRAG_PER_ORBIT=53.5; // metres apogee decay per orbit from drag
const J_BURN=0.313; // Ns — same as pendulum experiment
const DV=J_BURN/M;  // m/s per burn
const DH_APO=2*r0*DV/vc0; // 46.55m apogee rise per burn

// ── Experiment state machine ──────────────────────────────────────────────────
// Orbit simulation: track apogee altitude per orbit
function makeExp(){
  return {
    orbit:0,
    apogee:450000, // metres above Earth
    perigee:450000,
    phase:'phase0', // phase0|phase1|phase2a|pause|phase2b|phase3_wrong
    burns:0,
    propUsed:0,
    log:[{orbit:0,apo:450000,event:'start',burn:false,phase:'phase0'}],
    // Pendulum comparison
    pendAmplitude:7.97, // cm starting at 15°
    pendStrokes:0,
  };
}

function runOrbit(s, burnMode){
  let {orbit,apogee,perigee,burns,propUsed,log,pendAmplitude,pendStrokes,phase}=s;
  orbit++;

  // Natural drag decay every orbit
  const decay=DRAG_PER_ORBIT;
  apogee-=decay;
  perigee-=decay*0.3; // perigee decays less (higher density averages out)

  // Burn logic based on mode
  let event='coast';
  let fired=false;
  let dApo=0;

  if(burnMode==='perigee'){
    // Correct resonant burn at perigee
    dApo=DH_APO;
    apogee+=dApo;
    burns++;
    propUsed+=M*(1-Math.exp(-DV/(ISP*G0)))*1000;
    event='burn_perigee';
    fired=true;
    // Pendulum equivalent: push at turning point
    const J_pend=0.313;
    pendAmplitude+=J_pend**2/(2*0.5*9.81)*100; // cm
    pendStrokes++;
  } else if(burnMode==='apogee'){
    // Wrong phase — fires at apogee (less efficient, Oberth penalty)
    const efficiency=Math.PI/4; // random phase efficiency
    dApo=DH_APO*efficiency*0.6; // raises perigee instead, less effective
    perigee+=dApo;
    burns++;
    propUsed+=M*(1-Math.exp(-DV/(ISP*G0)))*1000;
    event='burn_apogee_wrong';
    fired=true;
  }
  // else: no burn (coast / pause)

  const newLog=[...log.slice(-199),{
    orbit,
    apo:apogee,
    peri:perigee,
    event,
    burn:fired,
    phase,
    dApo:fired?dApo:0,
    prop:propUsed,
    pendAmp:pendAmplitude,
    burns
  }];

  return{...s,orbit,apogee,perigee,burns,propUsed,log:newLog,
    pendAmplitude,pendStrokes};
}

// ── Apogee chart ──────────────────────────────────────────────────────────────
function ApoChart({log, W=420, H=180, phase}){
  if(log.length<2)return null;

  const apos=log.map(l=>l.apo/1000); // km
  const pends=log.map(l=>l.pendAmp||7.97);
  const orbits=log.map(l=>l.orbit);
  const lo_apo=Math.min(...apos)-0.05;
  const hi_apo=Math.max(...apos)+0.05;
  const rng_apo=hi_apo-lo_apo||0.1;
  const lo_p=Math.min(...pends)-1;
  const hi_p=Math.max(...pends)+1;
  const rng_p=hi_p-lo_p||1;
  const n=log.length;
  const maxOrb=Math.max(...orbits)||1;

  // Map to SVG
  const px=(orb)=>(orb/maxOrb)*(W-60)+30;
  const py_apo=(v)=>H-20-((v-lo_apo)/rng_apo)*(H-40);
  const py_pend=(v)=>H-20-((v-lo_p)/rng_p)*(H-40);

  // Apogee line
  const apoLine=log.map(l=>`${px(l.orbit)},${py_apo(l.apo/1000)}`).join(' ');
  // Pendulum amplitude line (normalised to same visual space)
  const pendLine=log.map(l=>`${px(l.orbit)},${py_pend(l.pendAmp||7.97)}`).join(' ');

  // Burn dots
  const burnDots=log.filter(l=>l.burn&&l.event==='burn_perigee');
  const wrongDots=log.filter(l=>l.burn&&l.event==='burn_apogee_wrong');

  // Phase bands
  const phases=[
    {id:'phase0',label:'Phase 0\nCalibrate',col:'#1a3a2a'},
    {id:'phase1',label:'Phase 1\nSingle',col:'#1a2a3a'},
    {id:'phase2a',label:'Phase 2a\nResonant',col:'#0a2a1a'},
    {id:'pause',label:'Pause',col:'#2a2a1a'},
    {id:'phase2b',label:'Phase 2b\nResume',col:'#0a2a1a'},
    {id:'phase3_wrong',label:'Phase 3\nWrong phase',col:'#2a1a1a'},
  ];

  // Find phase transitions in log
  let lastPhase='', phaseRanges=[];
  log.forEach((l,i)=>{
    if(l.phase!==lastPhase){
      if(phaseRanges.length>0) phaseRanges[phaseRanges.length-1].end=l.orbit;
      phaseRanges.push({id:l.phase,start:l.orbit,end:maxOrb});
      lastPhase=l.phase;
    }
  });

  return(
    <svg width={W} height={H} style={{display:'block'}}>
      {/* Phase bands */}
      {phaseRanges.map((r,i)=>{
        const ph=phases.find(p=>p.id===r.id)||{col:'#1a2a1a',label:r.id};
        return(
          <rect key={i} x={px(r.start)} y={20}
            width={px(r.end)-px(r.start)} height={H-40}
            fill={ph.col} opacity="0.5"/>
        );
      })}

      {/* Grid */}
      <line x1={30} y1={20} x2={30} y2={H-20} stroke="#1a3a2a" strokeWidth="0.5"/>
      <line x1={30} y1={H-20} x2={W-30} y2={H-20} stroke="#1a3a2a" strokeWidth="0.5"/>

      {/* Y axis labels */}
      <text x={28} y={22} fill="#0d9e6a" fontSize="7" fontFamily="monospace" textAnchor="end">
        {hi_apo.toFixed(3)}km
      </text>
      <text x={28} y={H-20} fill="#0d9e6a" fontSize="7" fontFamily="monospace" textAnchor="end">
        {lo_apo.toFixed(3)}km
      </text>

      {/* Pendulum amplitude line (dashed, secondary axis) */}
      <polyline points={pendLine} fill="none" stroke="#e2884a"
        strokeWidth="1" strokeDasharray="3 3" opacity="0.7"/>

      {/* Apogee line */}
      <polyline points={apoLine} fill="none" stroke="#00ff88"
        strokeWidth="1.8" strokeLinejoin="round"/>

      {/* Burn dots — perigee (correct) */}
      {burnDots.map((l,i)=>(
        <circle key={i} cx={px(l.orbit)} cy={py_apo(l.apo/1000)} r={4}
          fill="#00ff88" stroke="#050e10" strokeWidth="0.5"/>
      ))}

      {/* Burn dots — wrong phase */}
      {wrongDots.map((l,i)=>(
        <circle key={i} cx={px(l.orbit)} cy={py_apo(l.apo/1000)} r={4}
          fill="#e24a4a" stroke="#050e10" strokeWidth="0.5"/>
      ))}

      {/* Phase labels */}
      {phaseRanges.map((r,i)=>{
        const ph=phases.find(p=>p.id===r.id)||{label:r.id};
        const midX=px((r.start+r.end)/2);
        return(
          <text key={i} x={midX} y={30} fill="#4a7a6a"
            fontSize="6" fontFamily="monospace" textAnchor="middle">
            {ph.label.split('\n')[0]}
          </text>
        );
      })}

      {/* Legend */}
      <circle cx={W-80} cy={12} r={3} fill="#00ff88"/>
      <text x={W-74} y={15} fill="#00ff88" fontSize="7" fontFamily="monospace">
        apogee (km)
      </text>
      <line x1={W-80} y1={24} x2={W-70} y2={24} stroke="#e2884a"
        strokeWidth="1" strokeDasharray="3 2"/>
      <text x={W-66} y={27} fill="#e2884a" fontSize="7" fontFamily="monospace">
        pend amp
      </text>
    </svg>
  );
}

// ── Pendulum vs orbit side by side ────────────────────────────────────────────
function ComparePanel({exp}){
  const last=exp.log[exp.log.length-1];
  const apo_km=(exp.apogee/1000).toFixed(4);
  const rise=(exp.apogee-450000).toFixed(1);
  const acc='#0d9e6a',hi='#00ff88',warn='#e2c84a',mut='#4a7a6a',txt='#b0d0c0';

  return(
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
      {/* Pendulum */}
      <div style={{background:'#0b1a1e',border:'0.5px solid #1a3a30',
        borderRadius:5,padding:'10px'}}>
        <div style={{fontSize:8,color:mut,marginBottom:8,letterSpacing:'0.1em'}}>
          PENDULUM (LAB)
        </div>
        <svg width={160} height={140} style={{display:'block',margin:'0 auto'}}>
          {/* Wire + bob at current amplitude */}
          {(()=>{
            const cx=80,py=20,L=100;
            const maxAmp=exp.pendAmplitude/100; // metres
            const theta=Math.acos(Math.max(-1,Math.min(1,1-maxAmp/2.34)));
            const bx=cx+Math.sin(theta)*L;
            const by=py+Math.cos(theta)*L;
            return(<>
              <line x1={cx} y1={py} x2={cx} y2={py+L}
                stroke="#1a3a2a" strokeWidth="0.5" strokeDasharray="2 3"/>
              <line x1={cx} y1={py} x2={bx} y2={by}
                stroke="#3a6a5a" strokeWidth="1.5"/>
              {/* Height bracket */}
              <line x1={cx+50} y1={py+L} x2={cx+50} y2={by}
                stroke={acc} strokeWidth="0.5"/>
              <text x={cx+54} y={(py+L+by)/2} fill={hi} fontSize="8"
                fontFamily="monospace" dominantBaseline="middle">
                {exp.pendAmplitude.toFixed(2)}cm
              </text>
              <circle cx={bx} cy={by} r={10} fill="#0a2a1a" stroke={hi} strokeWidth="1.5"/>
              <text x={cx} y={py+L+12} fill={mut} fontSize="7"
                fontFamily="monospace" textAnchor="middle">⟵ 2.34m wire ⟶</text>
              <line x1={cx-40} y1={py+L+2} x2={cx+40} y2={py+L+2}
                stroke="#1a3a2a" strokeWidth="0.5"/>
            </>);
          })()}
        </svg>
        <div style={{fontSize:9,color:txt,textAlign:'center',marginTop:4}}>
          <div>Strokes: <span style={{color:hi}}>{exp.pendStrokes}</span></div>
          <div>Amplitude: <span style={{color:hi}}>{exp.pendAmplitude.toFixed(2)}cm</span></div>
          <div style={{fontSize:8,color:mut}}>+{(exp.pendAmplitude-7.97).toFixed(2)}cm gained</div>
        </div>
      </div>

      {/* Orbit */}
      <div style={{background:'#0b1a1e',border:'0.5px solid #1a3a30',
        borderRadius:5,padding:'10px'}}>
        <div style={{fontSize:8,color:mut,marginBottom:8,letterSpacing:'0.1em'}}>
          ORBIT (SPACE)
        </div>
        <svg width={160} height={140} style={{display:'block',margin:'0 auto'}}>
          {(()=>{
            const cx=80,cy=75;
            const er=28; // Earth radius in px
            const r_base=(RE+450e3);
            const r_peri_px=er+(exp.perigee/r_base)*(50-er);
            const r_apo_px =er+(exp.apogee /r_base)*(50-er)*1.01;
            const a_px=(r_peri_px+r_apo_px)/2;
            const c_px=a_px-r_peri_px;
            const b_px=Math.sqrt(Math.max(0,a_px**2-c_px**2));
            return(<>
              <circle cx={cx} cy={cy} r={er} fill="#060f14" stroke="#0a2a3e" strokeWidth="1"/>
              <text x={cx} y={cy+3} fill="#0a3a5e" fontSize="6"
                fontFamily="monospace" textAnchor="middle">EARTH</text>
              {/* 450km reference */}
              <circle cx={cx} cy={cy} r={er+(450000/r_base)*(50-er)}
                fill="none" stroke="#1a3a2a" strokeWidth="0.5" strokeDasharray="2 3"/>
              {/* Current orbit ellipse */}
              <ellipse cx={cx-c_px} cy={cy} rx={a_px} ry={b_px}
                fill="none" stroke={acc} strokeWidth="1.2"/>
              {/* Apogee dot */}
              <circle cx={cx-r_apo_px} cy={cy} r={3} fill={hi}/>
              <text x={cx-r_apo_px-4} y={cy-6} fill={hi} fontSize="6"
                fontFamily="monospace" textAnchor="end">
                apo
              </text>
              {/* Perigee dot */}
              <circle cx={cx+r_peri_px} cy={cy} r={3} fill={acc}/>
              {/* Rise bracket */}
              <text x={cx-r_apo_px-4} y={cy+8} fill={hi} fontSize="6"
                fontFamily="monospace" textAnchor="end">
                +{(exp.apogee-450000).toFixed(0)}m
              </text>
            </>);
          })()}
        </svg>
        <div style={{fontSize:9,color:txt,textAlign:'center',marginTop:4}}>
          <div>Burns: <span style={{color:hi}}>{exp.burns}</span></div>
          <div>Apogee: <span style={{color:hi}}>{apo_km}km</span></div>
          <div style={{fontSize:8,color:mut}}>+{rise}m gained</div>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function App(){
  const [exp,setExp]=useState(makeExp());
  const [autoRun,setAutoRun]=useState(false);
  const [currentPhase,setCurrentPhase]=useState('phase0');
  const [orbitQueue,setOrbitQueue]=useState([]);
  const [speed,setSpeed]=useState(1); // orbits per second
  const expRef=useRef(exp),phaseRef=useRef(currentPhase);
  const qRef=useRef(orbitQueue);
  expRef.current=exp; phaseRef.current=currentPhase; qRef.current=orbitQueue;
  const rafRef=useRef(),lastRef=useRef(null);
  const [tab,setTab]=useState('exp');

  // Build experiment sequence
  const buildSequence=()=>{
    const seq=[];
    // Phase 0: 5 calibration orbits (no burn)
    for(let i=0;i<5;i++) seq.push({phase:'phase0',burn:'none'});
    // Phase 1: 1 single burn
    seq.push({phase:'phase1',burn:'perigee'});
    // Phase 2a: 7 consecutive resonant burns
    for(let i=0;i<7;i++) seq.push({phase:'phase2a',burn:'perigee'});
    // Pause: 5 orbits no burn
    for(let i=0;i<5;i++) seq.push({phase:'pause',burn:'none'});
    // Phase 2b: 6 more resonant burns
    for(let i=0;i<6;i++) seq.push({phase:'phase2b',burn:'perigee'});
    // Phase 3: wrong phase comparison — 5 burns at apogee
    for(let i=0;i<5;i++) seq.push({phase:'phase3_wrong',burn:'apogee'});
    return seq;
  };

  const startExp=()=>{
    setExp(makeExp());
    setOrbitQueue(buildSequence());
    setAutoRun(true);
    setCurrentPhase('phase0');
  };

  const stopExp=()=>{ setAutoRun(false); };

  // Fire one orbit from queue
  const fireOrbit=useCallback(()=>{
    const q=qRef.current;
    if(q.length===0){ setAutoRun(false); return; }
    const [next,...rest]=q;
    setOrbitQueue(rest);
    setCurrentPhase(next.phase);
    setExp(prev=>{
      const ns=runOrbit({...prev,phase:next.phase},next.burn);
      return ns;
    });
  },[]);

  useEffect(()=>{
    if(!autoRun){ cancelAnimationFrame(rafRef.current); return; }
    const interval=1000/speed;
    let last=0;
    const tick=(ts)=>{
      if(ts-last>=interval){ last=ts; fireOrbit(); }
      rafRef.current=requestAnimationFrame(tick);
    };
    rafRef.current=requestAnimationFrame(tick);
    return()=>cancelAnimationFrame(rafRef.current);
  },[autoRun,speed,fireOrbit]);

  const bg='#050e10',card='#0b1a1e',bord='#1a3a30';
  const acc='#0d9e6a',hi='#00ff88',mut='#4a7a6a',txt='#b0d0c0';
  const warn='#e2c84a',red='#e24a4a',blue='#6ad4ff';

  const phaseInfo={
    phase0:{label:'Phase 0 — Calibration',col:mut,
      desc:'No burns. Measuring natural orbit decay from atmospheric drag. Establishing baseline.'},
    phase1:{label:'Phase 1 — Single burn',col:blue,
      desc:'One prograde burn at perigee. Proves single impulse raises apogee. Orbital equivalent of first pendulum push.'},
    phase2a:{label:'Phase 2a — Resonant accumulation',col:hi,
      desc:'Every perigee pass fires. Apogee rising linearly. This IS the pendulum experiment in orbit.'},
    pause:{label:'Pause — Energy stored',col:warn,
      desc:'No burns. Orbit holds its new energy. Gravity stores it — exactly like pendulum between pushes.'},
    phase2b:{label:'Phase 2b — Resume accumulation',col:hi,
      desc:'Burns resume. Apogee continues rising at same rate. Proves energy was stored, not lost.'},
    phase3_wrong:{label:'Phase 3 — Wrong phase test',col:red,
      desc:'Deliberately firing at APOGEE instead of PERIGEE. Less altitude gain proves resonant timing matters.'},
  };

  const phInfo=phaseInfo[currentPhase]||phaseInfo.phase0;
  const apo_rise=(exp.apogee-450000);
  const pend_rise=exp.pendAmplitude-7.97;

  return(
    <div style={{background:bg,minHeight:'100vh',color:txt,
      fontFamily:"'Courier New',monospace",padding:14}}>

      <div style={{textAlign:'center',marginBottom:12}}>
        <div style={{fontSize:9,color:mut,letterSpacing:'0.2em',marginBottom:2}}>
          SPACEMANCRAFT · EP-ADCS
        </div>
        <div style={{fontSize:15,color:hi}}>Orbital Pendulum Experiment</div>
        <div style={{fontSize:9,color:mut,marginTop:1}}>
          How to replicate the 1cm pendulum raise in orbit using EP-ADCS
        </div>
      </div>

      <div style={{display:'flex',gap:6,marginBottom:10,justifyContent:'center',flexWrap:'wrap'}}>
        {[{id:'exp',l:'Experiment'},{id:'design',l:'Experiment Design'},{id:'proof',l:'What It Proves'}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{
            background:tab===t.id?acc:'transparent',color:tab===t.id?'#000':mut,
            border:`1px solid ${tab===t.id?acc:bord}`,borderRadius:3,
            padding:'3px 14px',cursor:'pointer',fontSize:9
          }}>{t.l}</button>
        ))}
      </div>

      {tab==='exp'&&<>
        {/* Controls */}
        <div style={{background:card,border:`1px solid ${bord}`,borderRadius:5,
          padding:'8px 12px',marginBottom:8}}>
          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            <button onClick={startExp} style={{
              background:hi,color:'#000',border:'none',borderRadius:3,
              padding:'7px 20px',cursor:'pointer',fontSize:10,fontWeight:'bold'
            }}>▶ RUN FULL EXPERIMENT</button>
            <button onClick={stopExp} style={{
              background:'transparent',color:mut,border:`1px solid ${bord}`,
              borderRadius:3,padding:'7px 14px',cursor:'pointer',fontSize:10
            }}>⏸ PAUSE</button>
            <button onClick={fireOrbit} disabled={orbitQueue.length===0}
              style={{background:'transparent',color:blue,
                border:`1px solid ${blue}`,borderRadius:3,
                padding:'7px 12px',cursor:'pointer',fontSize:10}}>
              NEXT ORBIT →
            </button>
            <div style={{display:'flex',alignItems:'center',gap:6,marginLeft:'auto'}}>
              <span style={{fontSize:8,color:mut}}>Speed:</span>
              <input type="range" min="1" max="20" step="1" value={speed}
                onChange={e=>setSpeed(+e.target.value)}
                style={{width:60,accentColor:acc}}/>
              <span style={{fontSize:9,color:acc}}>{speed} orb/s</span>
            </div>
          </div>
        </div>

        {/* Phase indicator */}
        <div style={{background:card,border:`1px solid ${phInfo.col}`,borderRadius:5,
          padding:'8px 12px',marginBottom:8,transition:'border-color 0.3s'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
            <div style={{fontSize:9,color:phInfo.col,fontWeight:'bold'}}>
              {phInfo.label}
            </div>
            <div style={{fontSize:8,color:mut}}>
              Orbit {exp.orbit} · {orbitQueue.length} remaining
            </div>
          </div>
          <div style={{fontSize:9,color:txt,lineHeight:1.6}}>{phInfo.desc}</div>
          {/* Progress bar */}
          <div style={{marginTop:6,background:'#060f0a',height:6,borderRadius:3,
            border:'0.5px solid #1a3a2a',overflow:'hidden'}}>
            <div style={{
              width:`${(1-orbitQueue.length/buildSequence().length)*100}%`,
              height:'100%',background:phInfo.col,borderRadius:3,
              transition:'width 0.3s'
            }}/>
          </div>
        </div>

        {/* Main chart */}
        <div style={{background:card,border:`1px solid ${bord}`,borderRadius:5,
          padding:'8px 12px',marginBottom:8}}>
          <div style={{fontSize:8,color:mut,marginBottom:4,letterSpacing:'0.1em'}}>
            APOGEE ALTITUDE vs ORBIT NUMBER — GREEN=CORRECT BURNS · RED=WRONG PHASE BURNS
          </div>
          <ApoChart log={exp.log} W={420} H={180} phase={currentPhase}/>
        </div>

        {/* Side by side comparison */}
        <div style={{marginBottom:8}}>
          <div style={{fontSize:8,color:mut,marginBottom:6,letterSpacing:'0.1em'}}>
            PENDULUM (LAB) ↔ ORBIT (SPACE) — SAME PHYSICS
          </div>
          <ComparePanel exp={exp}/>
        </div>

        {/* Telemetry */}
        <div style={{background:card,border:`1px solid ${bord}`,borderRadius:5,padding:'8px 12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:5,fontSize:9}}>
            {[
              {l:'Orbit #',    v:exp.orbit,                           c:mut},
              {l:'Apogee',     v:`${(exp.apogee/1000).toFixed(4)}km`, c:hi},
              {l:'Apogee rise',v:`+${apo_rise.toFixed(1)}m`,          c:hi},
              {l:'Burns fired',v:exp.burns,                           c:acc},
              {l:'Prop used',  v:`${exp.propUsed.toFixed(3)}g`,        c:warn},
              {l:'Pend amp',   v:`${exp.pendAmplitude.toFixed(2)}cm`, c:'#e2884a'},
              {l:'Pend rise',  v:`+${pend_rise.toFixed(2)}cm`,        c:'#e2884a'},
              {l:'Phase',      v:currentPhase,                        c:phInfo.col},
            ].map(({l,v,c})=>(
              <div key={l} style={{background:'#060f0a',borderRadius:3,padding:'4px 6px'}}>
                <div style={{fontSize:7,color:mut,marginBottom:1}}>{l}</div>
                <div style={{fontSize:9,color:c,fontFamily:'monospace',fontWeight:'bold'}}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </>}

      {tab==='design'&&(
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {[
            {n:'Phase 0',c:mut,orbits:'Orbits 1–5',title:'Calibration — no burns',
             body:`Measure natural orbit decay from atmospheric drag.
Expected decay: 53.5 metres apogee per orbit at 450km.
This is the baseline. Confirms GPS is working and
establishes the "zero" for the experiment.

Equivalent: watching pendulum decay naturally without any push.

What to record: apogee altitude each orbit from GPS.
Success: decay rate matches atmospheric model within 20%.`},
            {n:'Phase 1',c:blue,orbits:'Orbit 6',title:'Single burn — first pendulum push',
             body:`Fire ONE prograde cold gas burn at perigee.
Impulse: 0.313 Ns (same as pendulum experiment)
Expected apogee rise: +46.5 metres

This is the first push of the pendulum.
GPS should show apogee jumping by ~46.5m in one orbit.
Signal/noise ratio: 4.7x (clearly detectable above GPS noise).

What to record: before and after apogee.
Success: Δapogee = 46.5 ± 10m.`},
            {n:'Phase 2a',c:hi,orbits:'Orbits 7–13',title:'Resonant accumulation — the main test',
             body:`Fire same 0.313 Ns at EVERY perigee pass for 7 orbits.
Expected: apogee rises 46.5m each orbit, linearly.

After 7 burns: +325.5m above starting point.
This is the pendulum rising 1cm per stroke.

Plot apogee vs orbit number — must show straight line.
Slope = 46.55 m/orbit. R² > 0.95.

This IS the orbital version of your pendulum experiment.
Each perigee pass = one swing of the pendulum.
Each burn = one repulsion stroke at the turning point.`},
            {n:'Pause',c:warn,orbits:'Orbits 14–18',title:'No burns — energy stored by orbit',
             body:`Stop firing for 5 orbits. Just coast.
Expected: apogee stays approximately constant (minus drag decay).

This proves orbital mechanics STORES the energy between burns
— exactly like gravity stores pendulum energy between strokes.

If apogee holds: energy storage confirmed.
This is the key proof that the orbital pendulum analogy is correct.`},
            {n:'Phase 2b',c:hi,orbits:'Orbits 19–24',title:'Resume — energy accumulates again',
             body:`Resume firing at perigee. Same 0.313 Ns each pass.
Expected: apogee continues rising at same 46.5m/orbit rate.

Starting from where pause left off, not from zero.
This proves: energy is permanently stored in orbit.
Pendulum equivalent: resuming pushes from current amplitude.`},
            {n:'Phase 3',c:red,orbits:'Orbits 25–29',title:'Wrong phase — Oberth proof',
             body:`Deliberately fire at APOGEE instead of perigee.
Same 0.313 Ns impulse. Different orbital phase.
Expected rise per burn: 36.5m (78% of perigee burn).

Ratio of correct/wrong: should be ≈ 4/π = 1.27.
This proves resonant timing matters — Oberth effect is real.

Pendulum equivalent: pushing at the BOTTOM instead of top.
Gets less height gain per joule of energy.
This is the control experiment that validates the whole design.`},
          ].map(r=>(
            <div key={r.n} style={{background:card,border:`1px solid ${r.c}40`,
              borderRadius:5,padding:'10px 12px'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                <div style={{fontSize:10,color:r.c,fontWeight:'bold'}}>
                  {r.n}: {r.title}
                </div>
                <div style={{fontSize:8,color:mut}}>{r.orbits}</div>
              </div>
              <pre style={{fontSize:9,color:txt,margin:0,lineHeight:1.8,whiteSpace:'pre-wrap'}}>
                {r.body}
              </pre>
            </div>
          ))}
        </div>
      )}

      {tab==='proof'&&(
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          <div style={{background:'#0a2a1a',border:`1px solid ${hi}`,
            borderRadius:5,padding:'10px 12px'}}>
            <div style={{fontSize:9,color:hi,marginBottom:6,letterSpacing:'0.1em'}}>
              WHAT THE EXPERIMENT PROVES
            </div>
            <div style={{fontSize:9,color:txt,lineHeight:1.9}}>
              If the experiment succeeds, you have demonstrated in orbit:
            </div>
          </div>

          {[
            {n:'1',c:hi,title:'Resonant impulse accumulates linearly',
             proof:`Pendulum result: amplitude grew 1cm per stroke
Orbital result:  apogee grows 46.5m per perigee burn
Both show: ΔE = constant per timed impulse
This IS the same physics at different scales.`,
             claim:`"EP-ADCS delivers repeatable, resonant orbital energy
injection — proven in orbit on SPACEMANCRAFT-1."`},
            {n:'2',c:acc,title:'Gravity stores energy between impulses',
             proof:`Pendulum result: amplitude holds between strokes
Orbital result:  apogee holds during 5-orbit pause
Both show: the restoring force (gravity) stores energy.
No energy is lost between burns — it is permanently stored
as orbital energy exactly as pendulum energy is stored as height.`,
             claim:`"Orbital mechanics provides the same energy storage function
as gravity in the pendulum experiment — validated in flight."`},
            {n:'3',c:warn,title:'Timing matters — Oberth effect confirmed',
             proof:`Perigee burn:  +46.5m per 0.313Ns
Apogee burn:   +36.5m per 0.313Ns (same impulse, wrong phase)
Ratio: 1.27 — matches Oberth prediction exactly.
Wrong-phase burns waste 21% of propellant for same orbital gain.
This proves EP-ADCS resonant timing saves real propellant.`,
             claim:`"Resonant perigee firing confirmed to save 21% propellant
vs non-resonant — Oberth effect validated experimentally in orbit."`},
            {n:'4',c:blue,title:'The propellant budget',
             proof:`Total experiment propellant: 9.1g
SPACEMANCRAFT-1 cold gas budget: ~1kg
Experiment uses 0.9% of total propellant budget.
This is an inexpensive in-orbit validation.
Results feed directly into the full orbit raising mission.`,
             claim:`"Full orbital pendulum experiment completed for 9.1g
propellant — 0.9% of mission budget."`},
          ].map(r=>(
            <div key={r.n} style={{background:card,
              border:`1px solid ${r.c}`,borderRadius:5,padding:'10px 12px'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                <div style={{width:22,height:22,borderRadius:'50%',background:r.c,
                  color:'#000',display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:10,fontWeight:'bold',flexShrink:0}}>{r.n}</div>
                <div style={{fontSize:10,color:r.c,fontWeight:'bold'}}>{r.title}</div>
              </div>
              <pre style={{fontSize:9,color:txt,margin:0,lineHeight:1.7,
                whiteSpace:'pre-wrap',marginBottom:8}}>{r.proof}</pre>
              <div style={{background:'#050e10',borderRadius:3,padding:'6px 8px',
                fontSize:9,color:r.c,fontStyle:'italic',borderLeft:`3px solid ${r.c}`}}>
                {r.claim}
              </div>
            </div>
          ))}

          <div style={{background:card,border:`1px solid ${bord}`,borderRadius:5,padding:'10px 12px'}}>
            <div style={{fontSize:9,color:hi,marginBottom:6}}>THE PUBLICATION CLAIM</div>
            <div style={{fontSize:10,lineHeight:1.9,color:txt,fontStyle:'italic',
              borderLeft:`3px solid ${hi}`,paddingLeft:10}}>
              "We demonstrate that the resonant impulse accumulation principle,
              validated in a ground-based pendulum experiment using the EP-ADCS
              electromagnetic-mechanical system, transfers directly to orbital
              mechanics. Perigee-timed cold gas burns of 0.313 Ns on
              SPACEMANCRAFT-1 produced linear apogee rise of 46.5 ± 10m per
              orbit across 14 consecutive perigee passes, confirming the
              pendulum-orbit equivalence and validating the EP-ADCS resonant
              orbit-raising architecture."
            </div>
          </div>
        </div>
      )}

      <div style={{textAlign:'center',marginTop:12,fontSize:8,color:mut}}>
        SPACEMANCRAFT PVT LTD · EP-ADCS · ORBITAL PENDULUM EXPERIMENT
      </div>
    </div>
  );
}
